/**
 * ---------------------------------------------------------------------
 * ChainForge — API Client (Axios)
 * ---------------------------------------------------------------------
 *
 * Goals:
 * - Centralize API configuration for the frontend
 * - Attach access JWT automatically on every authenticated request
 * - Refresh expired sessions silently using refresh token
 * - Keep auth handling explicit, predictable, and easy to maintain
 *
 * Session model:
 * - Access token  -> short-lived, sent in Authorization header
 * - Refresh token -> longer-lived, used only to obtain a new access token
 *
 * Current storage model (SaaS MVP):
 * - Tokens are stored in localStorage
 * - withCredentials=true is kept enabled so refresh can later move to
 *   HttpOnly cookies with minimal client refactor
 *
 * Important behavior:
 * - On 401, the client tries exactly one silent refresh
 * - If refresh succeeds, the original request is retried once
 * - If refresh fails, auth state is cleared cleanly
 *
 * This module stays UI-agnostic:
 * - no redirects
 * - no toasts
 * - no router dependencies
 */

import axios from "axios";
import { API_BASE_URL } from "../config";

/* ---------------------------------------------------------------------
 * Stable storage keys
 * ------------------------------------------------------------------- */

export const ACCESS_TOKEN_KEY = "cf_access_token";
export const REFRESH_TOKEN_KEY = "cf_refresh_token";
export const AUTH_ADDR_KEY = "cf_auth_address";

export const AUTH_CHANGED_EVENT = "chainforge:auth-changed";

function emitAuthChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
  }
}

/* ---------------------------------------------------------------------
 * Storage helpers
 * ------------------------------------------------------------------- */

function safeGetItem(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function safeSetItem(key, value) {
  try {
    const v = String(value || "").trim();
    if (!v) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, v);
  } catch {
    // ignore storage failures in restricted environments
  }
}

function safeRemoveItem(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/* ---------------------------------------------------------------------
 * Public auth helpers
 * ------------------------------------------------------------------- */

export function getAuthToken() {
  return safeGetItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  return safeGetItem(REFRESH_TOKEN_KEY);
}

export function getStoredAuthAddress() {
  return safeGetItem(AUTH_ADDR_KEY);
}

export function setAuthToken(token) {
  safeSetItem(ACCESS_TOKEN_KEY, token);
}

export function setRefreshToken(token) {
  safeSetItem(REFRESH_TOKEN_KEY, token);
}

export function setStoredAuthAddress(address) {
  safeSetItem(AUTH_ADDR_KEY, address);
}

export function setAuthSession({ accessToken, refreshToken, address } = {}) {
  setAuthToken(accessToken);
  setRefreshToken(refreshToken);
  setStoredAuthAddress(address);
  emitAuthChanged();
}

export function clearAuthSession() {
  safeRemoveItem(ACCESS_TOKEN_KEY);
  safeRemoveItem(REFRESH_TOKEN_KEY);
  safeRemoveItem(AUTH_ADDR_KEY);
  emitAuthChanged();
}

/* ---------------------------------------------------------------------
 * Axios instances
 * ------------------------------------------------------------------- */

// Normalize base URL to avoid accidental double slashes
const BASE = String(API_BASE_URL || "").replace(/\/+$/, "");

/**
 * Main authenticated API client used by the app.
 */
export const api = axios.create({
  baseURL: BASE,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  timeout: 30_000,
});

/**
 * Secondary bare client used specifically for refresh requests.
 * This avoids interceptor recursion when /auth/refresh itself fails.
 */
const refreshClient = axios.create({
  baseURL: BASE,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  timeout: 30_000,
});

/* ---------------------------------------------------------------------
 * Silent refresh coordination
 * ------------------------------------------------------------------- */

/**
 * Prevents multiple concurrent refresh calls when several requests
 * fail with 401 at the same time.
 */
let isRefreshing = false;

/**
 * Queue of requests waiting for refresh to complete.
 * Each queued item receives either:
 * - a fresh access token, or
 * - an error if refresh failed
 */
let refreshQueue = [];

function flushRefreshQueue(error, nextAccessToken = "") {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(nextAccessToken);
  });
  refreshQueue = [];
}

async function requestTokenRefresh() {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error("Missing refresh token.");
  }

  const res = await refreshClient.post("/auth/refresh", { refreshToken });
  const data = res?.data || {};

  if (!data?.ok || !data?.accessToken) {
    throw new Error(data?.error || "Refresh failed.");
  }

  setAuthSession({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken || refreshToken,
    address: data.address || getStoredAuthAddress(),
  });

  return data.accessToken;
}

/* ---------------------------------------------------------------------
 * Request interceptor
 * ------------------------------------------------------------------- */

// Attach Authorization header (if access token exists)
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/* ---------------------------------------------------------------------
 * Response interceptor
 * ------------------------------------------------------------------- */

/**
 * Behavior on 401:
 * - Ignore refresh flow for auth endpoints themselves
 * - Retry original request once after successful refresh
 * - Clear session if refresh cannot be completed
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const status = error?.response?.status;
    const requestUrl = String(originalRequest?.url || "");

    const isAuthRefreshCall = requestUrl.includes("/auth/refresh");
    const isAuthStartCall = requestUrl.includes("/auth/start");
    const isAuthVerifyCall = requestUrl.includes("/auth/verify");

    const shouldSkipRefresh =
      isAuthRefreshCall || isAuthStartCall || isAuthVerifyCall;

    if (status !== 401 || shouldSkipRefresh) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      clearAuthSession();
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearAuthSession();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      })
        .then((newAccessToken) => {
          originalRequest._retry = true;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        })
        .catch((queueError) => Promise.reject(queueError));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newAccessToken = await requestTokenRefresh();

      flushRefreshQueue(null, newAccessToken);

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      return api(originalRequest);
    } catch (refreshError) {
      flushRefreshQueue(refreshError, "");
      clearAuthSession();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
